/**
 * 全局态布局：左侧栏 7 项导航 + 内容区。
 *
 * 导航项全部由 nav.ts 推导，此处不写死任何菜单文字 —— 保证
 * "菜单 / 路由 / 全屏工作台里的镜像导航"三处永远一致。
 */
import { ChevronsLeft, ChevronsRight, Activity } from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { useAppCapability } from '../capabilityContext';
import { NAV_ITEMS } from '../nav';
import { UserPanel } from '../../features/user/UserPanel';
import { SupportDock } from '../../features/support/SupportDock';
import { MOCK_USER, userCreditGauge } from '../../model/user';

export function GlobalLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const { report } = useAppCapability();
  const creditPct = userCreditGauge(MOCK_USER).totalPct;

  return (
    <div className="flex h-full">
      <aside
        className="flex shrink-0 flex-col border-r border-surface-line bg-surface-raised
                   transition-[width] duration-200 ease-out"
        style={{ width: collapsed ? 56 : 208 }}
      >
        <div className="flex h-12 items-center gap-2 px-3">
          <img src="/logo.png" alt="logo" className="h-6 w-6 shrink-0 rounded" />
          {collapsed ? null : (
            <span className="truncate text-ui font-semibold">短剧创作 Pro</span>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.key}
              to={item.path}
              end={item.path === '/'}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                [
                  'mx-2 flex items-center gap-2.5 rounded-control px-2.5 py-1.5 text-ui',
                  'transition-colors',
                  isActive
                    ? 'bg-surface-overlay text-ink-primary'
                    : 'text-ink-secondary hover:bg-surface-overlay hover:text-ink-primary',
                ].join(' ')
              }
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-50" />
              {collapsed ? null : <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-surface-line p-2">
          {/* 运营/赛事入口（图片版，与首页活动卡联动） */}
          <NavLink
            to="/team"
            title="创作大赛 · 报名中"
            className="mb-1 block overflow-hidden rounded-control border border-surface-line"
          >
            <img
              src="/images/contest-sidebar.png"
              alt="创作大赛 · 报名中"
              className={`block transition-transform duration-200 hover:scale-[1.04] ${collapsed ? 'h-10 w-10 object-cover' : 'w-full'}`}
            />
          </NavLink>
          <NavLink
            to="/diagnostics"
            title="环境自检"
            className="mx-0.5 flex items-center gap-2.5 rounded-control px-2.5 py-1.5 text-uiSm
                       text-ink-muted hover:bg-surface-overlay hover:text-ink-secondary"
          >
            <Activity size={14} className="shrink-0" />
            {collapsed ? null : <span>环境自检</span>}
          </NavLink>

          {/*
            折叠开关：上一版只有 22×22 的灰色图标且无过渡动画，用户点了以为没反应。
            按计划书 §11.7 R-5 三条合格标准修正：热区≥ 28、有可见 hover 反馈、带 aria-expanded 与文字标签。
          */}
          <div className="mt-1 flex items-center gap-1 rounded-control border border-surface-line p-0.5">
            {collapsed ? null : (
              <span className="ml-1.5 text-uiSm text-ink-muted">
                画质 {report === null ? '…' : report.tier}
              </span>
            )}
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
              aria-expanded={!collapsed}
              title={collapsed ? '展开侧边栏' : '收起侧边栏'}
              className="ml-auto flex h-7 min-w-7 cursor-pointer items-center justify-center gap-1
                         rounded-control px-1.5 text-uiSm text-ink-secondary
                         transition-colors hover:bg-surface-overlay hover:text-ink-primary
                         focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            >
              {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
              {collapsed ? null : <span>收起</span>}
            </button>
          </div>

          {/* 用户信息入口（点开面板） */}
          <div className="relative mt-1">
            {userOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setUserOpen(false)} role="presentation" />
                <div className="absolute bottom-full left-0 z-40 mb-2">
                  <UserPanel user={MOCK_USER} onClose={() => setUserOpen(false)} />
                </div>
              </>
            )}
            <button
              type="button"
              onClick={() => setUserOpen((v) => !v)}
              aria-expanded={userOpen}
              title="用户信息"
              className="flex w-full items-center gap-2 rounded-control px-1.5 py-1.5 transition-colors hover:bg-surface-overlay"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/20 text-uiSm font-bold text-accent">{MOCK_USER.name.slice(0, 1)}</span>
              {collapsed ? null : (
                <span className="min-w-0 flex-1 text-left">
                  <span className="block truncate text-uiSm text-ink-primary">{MOCK_USER.name}</span>
                  <span className="block text-[11px] text-ink-faint">额度 {creditPct}%</span>
                </span>
              )}
            </button>
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-hidden">
        <Outlet />
      </main>
      <SupportDock />
    </div>
  );
}
